import type { AgenticLogicActiveRepairRun } from "@intelligems/ig-types/dtos/agentic-logic/repair";
import type { AgenticLogicRulePublishJobDto } from "@intelligems/ig-types/dtos/agentic-logic/publish";

import { useCancelAgenticLogicRuleChecks } from "@src/api/agentic-logic/cancel-agentic-logic-rule-checks";
import type { GateStripCancelAction } from "@src/features/agenticCodegen/workspace/components/02-GateRail/GateProgressStrip";

/**
 * The checks strip's "Cancel": which run it stops, and whether it can right now.
 *
 * While the repair loop owns the run, the job it is on is the one to stop —
 * the loop may be between jobs, writing a fix, with the tracked job already
 * failed. Otherwise it is the tracked job, while that is in flight.
 *
 * There is no target in the instant between starting a run and its first
 * frame, when the tracked job is still the previous, finished one. Cancel then
 * holds disabled rather than cancelling nothing and leaving the new run going.
 */
export const useCancelRuleChecks = ({
  ruleId,
  publishJob,
  repair,
}: {
  ruleId: string;
  publishJob: Pick<AgenticLogicRulePublishJobDto, "id" | "status"> | undefined;
  repair: { run: AgenticLogicActiveRepairRun | null; isActive: boolean };
}): GateStripCancelAction => {
  const cancelChecks = useCancelAgenticLogicRuleChecks();

  const targetJobId = repair.isActive
    ? (repair.run?.currentPublishJobId ?? null)
    : publishJob?.status === "queued" || publishJob?.status === "running"
      ? publishJob.id
      : null;

  return {
    isCancelling: cancelChecks.isPending || targetJobId === null,
    onCancel: () => {
      if (targetJobId === null || cancelChecks.isPending) {
        return;
      }
      cancelChecks.mutate({
        pathParams: { ruleId },
        queryParams: undefined,
        body: { publishJobId: targetJobId },
      });
    },
  };
};
